// The meters' read side (P4.7) — one rAF loop, one reader, one drain.
//
// ## ⚠️ Why every meter is driven from this one hook rather than from its own component
//
// The obvious shape is a `<Meter source={...}>` that reads the ring in its own `useEffect`, the way
// `Transport.tsx` drives the deck's. It would work, and it would quietly undo the prompt.
//
// P4.7's rule is *"one frame, not three readings"*, and `SignalReader.read()` advances a cursor: four
// meters with four readers would each drain a **different** set of frames from the same ring, so the
// master bar and the channel bars beside it would be describing different blocks — the exact
// incoherence the audio thread went to some trouble to prevent. `readFramePeaks` exists so that the
// consumer keeps the guarantee the producer made, and keeping it means one reader, drained once per
// animation frame, feeding everything.
//
// So this hook owns the loop and hands out refs. That is also why it returns refs rather than values:
// **no React state on the path** (Stack §2), the same way `Transport.tsx` writes `style.width`
// directly from its own rAF tick. A `useState` here would re-render the whole mixer sixty times a
// second to move four bars.
//
// ## ⚠️ The effect is keyed on `graph` alone, and that is deliberate
//
// P4.16's watchdog left the warning that a long-lived effect must never be keyed on a value a hook
// rebuilds every render, or the timer restarts before it ever fires. `graph` is the one input here
// that is stable for the life of the audio context; the refs are `useRef` objects, which React keeps
// identical across renders, so nothing in the dependency list changes on a re-render.

import { useEffect, useRef, type RefObject } from 'react';

import type { AudioGraph } from '../../audio/engine';
import {
  createMeterFrame,
  SignalReader,
  type MeterFrame,
} from '../../platform/sab/signal-bus';
import {
  gainReductionDb,
  gainReductionFraction,
  meterDb,
  meterFall,
  meterFraction,
  meterPeakHold,
  METER_FLOOR_DB,
  type PeakHoldState,
} from '../../platform/types/meter';
import { SIGNAL_SOURCE, type SignalSourceIndex } from '../../platform/types/rings';

/** The two elements one meter draws with: a bar for RMS and a thin marker for peak. */
export interface MeterHandle {
  readonly rms: RefObject<HTMLDivElement | null>;
  readonly peak: RefObject<HTMLDivElement | null>;
}

export interface MetersApi {
  /** Deck A's channel strip, pre-fader (FR-14). */
  readonly a: MeterHandle;
  /** Deck B's channel strip, pre-fader (FR-14). */
  readonly b: MeterHandle;
  /** The master bus, pre-limiter (FR-16). */
  readonly master: MeterHandle;
  /** What the limiter is taking off. Its own scale — see `GAIN_REDUCTION_MAX_DB`. */
  readonly gainReduction: RefObject<HTMLDivElement | null>;
}

/** One meter's ballistics state: where the bar and the marker are being drawn, in dB. */
interface MeterState {
  rmsDb: number;
  /** The marker's own state — it holds before it falls, where the bar does not. See `meterPeakHold`. */
  readonly peak: PeakHoldState;
}

function newState(): MeterState {
  return {
    rmsDb: METER_FLOOR_DB,
    peak: { db: METER_FLOOR_DB, holdSeconds: 0 },
  };
}

function useMeterHandle(): MeterHandle {
  const rms = useRef<HTMLDivElement | null>(null);
  const peak = useRef<HTMLDivElement | null>(null);
  return { rms, peak };
}

/**
 * Advances one meter and writes it to the DOM.
 *
 * Both rise instantly and fall at a constant dB/s — see `meter.ts`. ⚠️ **The marker additionally
 * holds at a new maximum for a second before it falls, and P4.7 deliberately did not do that.** The
 * comment here used to argue that the two should share ballistics so the gap between them stayed
 * readable as the crest factor. Then the meter was watched on real music and the marker flickered
 * while the bar looked right — peak is far spikier than RMS, so a 2 px line that falls immediately
 * chases every transient. `METER_PEAK_HOLD_SECONDS` carries the rest of it.
 */
function drawMeter(
  handle: MeterHandle,
  state: MeterState,
  source: SignalSourceIndex,
  frame: MeterFrame,
  dt: number,
): void {
  state.rmsDb = meterFall(state.rmsDb, meterDb(frame.rms[source] ?? 0), dt);
  meterPeakHold(state.peak, meterDb(frame.peak[source] ?? 0), dt);

  const bar = handle.rms.current;
  if (bar !== null) {
    bar.style.width = `${(meterFraction(state.rmsDb) * 100).toFixed(1)}%`;
  }
  const marker = handle.peak.current;
  if (marker !== null) {
    marker.style.left = `${(meterFraction(state.peak.db) * 100).toFixed(1)}%`;
  }
}

export function useMeters(graph: AudioGraph | null): MetersApi {
  const a = useMeterHandle();
  const b = useMeterHandle();
  const master = useMeterHandle();
  const gainReduction = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (graph === null) {
      return;
    }

    const reader = new SignalReader(graph.signals);
    // Allocated once, outside the loop, and refilled in place every frame — `createMeterFrame`
    // exists so this path allocates nothing per frame.
    const frame = createMeterFrame();
    const states = { a: newState(), b: newState(), master: newState() };
    let reductionDb = 0;
    let last = performance.now();
    let handle = 0;

    const tick = (now: number): void => {
      handle = requestAnimationFrame(tick);

      const dt = (now - last) / 1000;
      last = now;

      // ⚠️ **One drain for every meter below** — the consumer half of "one frame, not three
      // readings". Draining once per source would give each bar a different set of frames.
      //
      // The return value is deliberately unused: when no frame arrived, `readFramePeaks` leaves
      // `frame` holding the previous reading, so the bars **hold** rather than drop. That is
      // `Transport.tsx`'s established choice for the deck meter — "leave the bar where it is rather
      // than dropping it to zero and flickering" — and a single missed vsync is far more common than
      // audio actually stopping.
      //
      // ⚠️ The cost of holding, stated rather than discovered later: if the context is suspended
      // the meters freeze at their last level instead of falling to silence. Distinguishing a
      // dropped frame from a stopped engine needs a timeout this does not have. Parked in
      // BACKLOG-NOTES under P4.7; the fix is a staleness deadline, not a change to this call.
      reader.readFramePeaks(frame);

      drawMeter(a, states.a, SIGNAL_SOURCE.STRIP_A, frame, dt);
      drawMeter(b, states.b, SIGNAL_SOURCE.STRIP_B, frame, dt);
      drawMeter(master, states.master, SIGNAL_SOURCE.MASTER, frame, dt);

      // Gain reduction rides the same law in its own units. `frame.gainReduction` is the *lowest*
      // gain across the interval, so this is the hardest the limiter worked since the last vsync
      // rather than whatever it happened to be doing on the final block — and `meterFall` jumps
      // straight to a larger reduction, which is "rise is instantaneous" pointing downwards.
      reductionDb = meterFall(reductionDb, gainReductionDb(frame.gainReduction), dt);
      const reductionBar = gainReduction.current;
      if (reductionBar !== null) {
        reductionBar.style.width = `${(gainReductionFraction(reductionDb) * 100).toFixed(1)}%`;
      }
    };

    handle = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(handle);
    };
  }, [graph, a, b, master]);

  return { a, b, master, gainReduction };
}
